'use client'

import Link from 'next/link'
import { Heart, LogOut, MapPin, Medal, PackageSearch, Settings } from 'lucide-react'
import Avatar from 'boring-avatars'
import { formatImageURL, isLoyaltyActive } from '@/utils/util'
import { useProfileNav, type ProfileNavKey } from '@/lib/commerce/hooks'

const ICONS: Record<ProfileNavKey, any> = {
  orders: PackageSearch,
  loyalty: Medal,
  wishlist: Heart,
  addresses: MapPin,
  settings: Settings,
  logout: LogOut,
}

export default function ProfileCard({ customer, vendor, isLoading = false, setIsLoading, onLinkClick }: { customer: any, vendor: any, isLoading?: boolean, setIsLoading?: (isLoading: boolean) => void, onLinkClick?: () => void }) {
  void setIsLoading
  const { accountItems, logoutItem } = useProfileNav()
  const visibleItems = accountItems.filter(item => item.key !== 'loyalty' || isLoyaltyActive(vendor))

  if (isLoading) {
    return (
      <div className="p-5 flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 w-32 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-3 w-44 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 rounded-lg animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-5 flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full overflow-hidden shrink-0" style={{ background: 'linear-gradient(135deg, #2a2a30, #08080a)', border: '1px solid rgba(255,255,255,0.14)' }}>
          {customer?.avatar ? (
            <img src={formatImageURL(customer.avatar)} alt="profile" className="w-full h-full object-cover" />
          ) : (
            <Avatar size={46} variant="marble" name={customer?.email || undefined} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-white truncate" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
            {customer?.firstName || customer?.email?.split('@')[0] || 'Хэрэглэгч'} {customer?.lastName || ''}
          </div>
          {customer?.email && (
            <div className="text-[11.5px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>{customer.email}</div>
          )}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2.5" style={{ color: '#a8a8b3' }}>Бүртгэл</div>
        <nav className="flex flex-col gap-0.5">
          {visibleItems.map(item => {
            const Icon = ICONS[item.key]
            return (
              <Link
                key={item.key}
                href={item.url}
                onClick={onLinkClick}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-colors"
                style={{
                  background: item.isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
                  color: item.isActive ? 'white' : 'rgba(255,255,255,0.78)',
                }}
              >
                <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="flex-1">{item.name}</span>
              </Link>
            )
          })}
        </nav>
      </div>

      {logoutItem && (
        <div className="pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <Link
            href={logoutItem.url}
            onClick={onLinkClick}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-colors hover:bg-rose-500/10"
            style={{ color: '#fda4af' }}
          >
            <LogOut className="h-4 w-4 shrink-0" strokeWidth={2} />
            <span className="flex-1">{logoutItem.name}</span>
          </Link>
        </div>
      )}
    </div>
  )
}
